/**
 * Role Service
 *
 * Service for fetching role data from the Role table.
 * Roles are client-specific and used for the role dropdown in user forms.
 */

import { API } from 'aws-amplify';
import { listRoles as listRolesQuery } from '../../../../../graphql/queries';

/**
 * Fetch all roles for a specific client
 *
 * @param {string} clientId - The client ID to fetch roles for
 * @returns {Promise<Array>} Array of role objects with { clientId, role, description }
 * @throws {Error} If the GraphQL query fails
 */
export async function listRoles(clientId) {
  if (!clientId) {
    throw new Error('clientId is required to fetch roles');
  }

  try {
    const response = await API.graphql({
      query: listRolesQuery,
      variables: { clientId },
    });

    const roles = response.data.listRoles || [];

    // Sort roles alphabetically by role name
    roles.sort((a, b) => {
      const roleA = a.role || '';
      const roleB = b.role || '';
      return roleA.localeCompare(roleB);
    });

    return roles;
  } catch (error) {
    console.error('Error fetching roles:', error);
    throw new Error(`Failed to fetch roles for client ${clientId}: ${error.message}`);
  }
}

/**
 * Format roles for dropdown options
 *
 * @param {Array} roles - Array of role objects from listRoles
 * @returns {Array} Array of { value, label } objects for dropdown
 */
export function formatRolesForDropdown(roles) {
  if (!Array.isArray(roles)) {
    return [];
  }

  return roles
    .filter((role) => role.role) // Filter out roles without a role value
    .map((role) => ({
      value: role.role,
      label: role.description || role.role, // Use description if available, otherwise role name
    }));
}
